import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getResourceEntitlementLog} from 'src/openapi/admin/apis/EntitlementLogApi';
import {useAppDispatch} from 'src/store/hooks';

import {entitlementResourceChangeLogStateSelector} from '../slices/entitlement-resource-change-log-state.slice';
import {
    entitlementResourceChangeLogActions,
    entitlementResourceChangeLogDataSelector,
    entitlementResourceChangeLogResultSelector,
} from '../slices/entitlement-resource-change-log.slice';

import type {EntitlementResourceLogsCriteria} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementLogsPage} from 'src/openapi/admin/models/EntitlementLogsPage';


type GetEntitlementResourceLogCacheKeys = CacheKeys<undefined, undefined, EntitlementResourceLogsCriteria>;

type Props = {
    resourceId: string,
};

export const useGetEntitlementResourceChangeLog = ({resourceId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const getAction = useCallback((cacheKeys: GetEntitlementResourceLogCacheKeys) => apiDispatch(
        getResourceEntitlementLog({
            resourceId,
            resourceEntitlementLogsPageRequest: {
                pagination: {
                    pageSize: cacheKeys.pagination.pageSize,
                    currentPage: cacheKeys.pagination.currentPage,
                },
                actionName: cacheKeys.filters?.actionName as string | undefined,
                startDate: cacheKeys.filters?.startDate as string | undefined,
                endDate: cacheKeys.filters?.endDate as string | undefined,
                entitlementName: cacheKeys.filters?.entitlementName as string | undefined,
            },
        }),
    ), [resourceId, apiDispatch]);

    const setLoadedData = useCallback((
        response: EntitlementLogsPage,
        cacheKeys: GetEntitlementResourceLogCacheKeys,
    ) => {
        dispatch(entitlementResourceChangeLogActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: response.events,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: entitlementResourceChangeLogDataSelector,
        stateSelector: entitlementResourceChangeLogStateSelector,
        resultSelector: entitlementResourceChangeLogResultSelector,
        actions: entitlementResourceChangeLogActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
